import { callRpc } from './base';
import { ensureObject } from './util';

export interface UpdateProgramAiInstructionsParams {
  program_id: string;
  ai_instructions: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramAiInstructionsResult {
  program_id: string;
  program_code: string | null;
  ai_instructions: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramAiInstructions = async (
  params: UpdateProgramAiInstructionsParams
): Promise<UpdateProgramAiInstructionsResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_ai_instructions: params.ai_instructions,
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_ai_instructions', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramAiInstructionsResult>(
        data,
        'INVALID_AI_INSTRUCTIONS_RESPONSE',
        'update_program_ai_instructions RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export interface UpdateProgramStep0FlagsParams {
  program_id: string;
  qc_flag_preparation: boolean;
  qc_flag_preparation_notes: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramStep0FlagsResult {
  program_id: string;
  program_code: string | null;
  qc_flag_preparation: boolean;
  qc_flag_preparation_notes: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramStep0Flags = async (
  params: UpdateProgramStep0FlagsParams
): Promise<UpdateProgramStep0FlagsResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_qc_flag_preparation: params.qc_flag_preparation,
    p_qc_flag_preparation_notes: params.qc_flag_preparation_notes,
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_step0_flags', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramStep0FlagsResult>(
        data,
        'INVALID_STEP0_FLAGS_RESPONSE',
        'update_program_step0_flags RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export interface UpdateProgramRequirementsQcParams {
  program_id: string;
  qc_flag_requirements: boolean;
  qc_flag_requirements_notes: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramRequirementsQcResult {
  program_id: string;
  program_code: string | null;
  qc_flag_requirements: boolean;
  qc_flag_requirements_notes: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramRequirementsQc = async (
  params: UpdateProgramRequirementsQcParams
): Promise<UpdateProgramRequirementsQcResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_qc_flag_requirements: params.qc_flag_requirements,
    p_qc_flag_requirements_notes: params.qc_flag_requirements_notes,
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_requirements_qc', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramRequirementsQcResult>(
        data,
        'INVALID_REQUIREMENTS_QC_RESPONSE',
        'update_program_requirements_qc RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export interface UpdateProgramDeadlinesQcParams {
  program_id: string;
  qc_flag_deadlines: boolean;
  qc_flag_deadlines_notes: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramDeadlinesQcResult {
  program_id: string;
  program_code: string | null;
  qc_flag_deadlines: boolean;
  qc_flag_deadlines_notes: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramDeadlinesQc = async (
  params: UpdateProgramDeadlinesQcParams
): Promise<UpdateProgramDeadlinesQcResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_qc_flag_deadlines: params.qc_flag_deadlines,
    p_qc_flag_deadlines_notes: params.qc_flag_deadlines_notes,
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_deadlines_qc', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramDeadlinesQcResult>(
        data,
        'INVALID_DEADLINES_QC_RESPONSE',
        'update_program_deadlines_qc RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export interface UpdateProgramJudgingQcParams {
  program_id: string;
  qc_flag_judging: boolean;
  qc_flag_judging_notes: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramJudgingQcResult {
  program_id: string;
  program_code: string | null;
  qc_flag_judging: boolean;
  qc_flag_judging_notes: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramJudgingQc = async (
  params: UpdateProgramJudgingQcParams
): Promise<UpdateProgramJudgingQcResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_qc_flag_judging: params.qc_flag_judging,
    p_qc_flag_judging_notes: params.qc_flag_judging_notes,
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_judging_qc', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramJudgingQcResult>(
        data,
        'INVALID_JUDGING_QC_RESPONSE',
        'update_program_judging_qc RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export interface UpdateProgramDetailsQcParams {
  program_id: string;
  qc_flag_program_details: boolean;
  qc_flag_program_details_notes: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramDetailsQcResult {
  program_id: string;
  program_code: string | null;
  qc_flag_program_details: boolean;
  qc_flag_program_details_notes: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramDetailsQc = async (
  params: UpdateProgramDetailsQcParams
): Promise<UpdateProgramDetailsQcResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_qc_flag_program_details: params.qc_flag_program_details,
    p_qc_flag_program_details_notes: params.qc_flag_program_details_notes,
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_details_qc', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramDetailsQcResult>(
        data,
        'INVALID_PROGRAM_DETAILS_QC_RESPONSE',
        'update_program_details_qc RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export interface UpdateProgramVettingQcParams {
  program_id: string;
  qc_flag_vetting: boolean;
  qc_flag_vetting_notes: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramVettingQcResult {
  program_id: string;
  program_code: string | null;
  qc_flag_vetting: boolean;
  qc_flag_vetting_notes: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramVettingQc = async (
  params: UpdateProgramVettingQcParams
): Promise<UpdateProgramVettingQcResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_qc_flag_vetting: params.qc_flag_vetting,
    p_qc_flag_vetting_notes: params.qc_flag_vetting_notes,
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_vetting_qc', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramVettingQcResult>(
        data,
        'INVALID_VETTING_QC_RESPONSE',
        'update_program_vetting_qc RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export interface UpdateProgramDetailsDataParams {
  program_id: string;
  host_country: string | null;
  language_requirements: string | null;
  submission_type: string | null;
  has_audiobook_categories: boolean | null;
  bisac_default_primary_code: string | null;
  bisac_default_secondary_code: string | null;
  bisac_default_tertiary_code: string | null;
  bisac_preferred_edition_year: string | null;
  industry_warnings: string | null;
  editor_id?: string | null;
}

export interface UpdateProgramDetailsDataResult {
  program_id: string;
  program_code: string | null;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export const updateProgramDetailsData = async (
  params: UpdateProgramDetailsDataParams
): Promise<UpdateProgramDetailsDataResult> => {
  const rpcParams: Record<string, unknown> = {
    p_program_id: params.program_id,
    p_host_country: params.host_country,
    p_language_requirements: params.language_requirements,
    p_submission_type: params.submission_type,
    p_has_audiobook_categories: params.has_audiobook_categories,
    p_bisac_default_primary_code: params.bisac_default_primary_code,
    p_bisac_default_secondary_code: params.bisac_default_secondary_code,
    p_bisac_default_tertiary_code: params.bisac_default_tertiary_code,
    p_bisac_preferred_edition_year: params.bisac_preferred_edition_year,
    p_industry_warnings: params.industry_warnings
  };

  if (params.editor_id !== undefined) {
    rpcParams.p_editor_id = params.editor_id;
  }

  return await callRpc('update_program_details_data', {
    params: rpcParams,
    transform: (data) =>
      ensureObject<UpdateProgramDetailsDataResult>(
        data,
        'INVALID_PROGRAM_DETAILS_DATA_RESPONSE',
        'update_program_details_data RPC returned invalid data',
        { program_id: params.program_id }
      ),
    successMeta: (result) => ({
      program_id: result.program_id,
      changed_fields: Object.keys(result.changes ?? {}).length
    })
  });
};

export default updateProgramAiInstructions;
